import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  CreateEventHubWebhookSubscriptionRequest,
  EventHubApiService,
  EventHubWebhookSubscriptionListItem
} from './event-hub-api.service';

@Component({
  standalone: true,
  selector: 'ain-event-hub-webhook-subscriptions',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    CheckboxModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('eventHub.webhooks.title')" [description]="t('eventHub.webhooks.description')">
      <div page-actions>
        <button pButton type="button" [label]="t('common.create')" (click)="createDialogVisible.set(true)"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!rows().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.name') }}</th>
              <th>{{ t('eventHub.catalog.fields.eventType') }}</th>
              <th>{{ t('eventHub.webhooks.fields.endpoint') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.name }}</td>
              <td>{{ row.eventType }}</td>
              <td>{{ row.endpointUrl }}</td>
              <td>{{ row.isActive ? t('eventHub.webhooks.active') : t('eventHub.webhooks.inactive') }}</td>
              <td><a [routerLink]="['/event-hub/webhook-subscriptions', row.id]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog
        [header]="t('eventHub.webhooks.create.title')"
        [visible]="createDialogVisible()"
        [modal]="true"
        [style]="{ width: '42rem', maxWidth: '95vw' }"
        (visibleChange)="createDialogVisible.set($event)">
        <section class="form-grid">
          <label>
            <span>{{ t('columns.name') }}</span>
            <input pInputText [(ngModel)]="createModel.name" />
          </label>
          <label>
            <span>{{ t('eventHub.catalog.fields.eventType') }}</span>
            <input pInputText [(ngModel)]="createModel.eventType" />
          </label>
          <label>
            <span>{{ t('eventHub.webhooks.fields.endpoint') }}</span>
            <input pInputText [(ngModel)]="createModel.endpointUrl" />
          </label>
          <label>
            <span>{{ t('eventHub.webhooks.fields.allowedDomains') }}</span>
            <input pInputText [(ngModel)]="createModel.allowedDomainsText" [placeholder]="t('eventHub.webhooks.fields.allowedDomainsPlaceholder')" />
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="createModel.deliverPii" [binary]="true" />
            <span>{{ t('eventHub.webhooks.fields.deliverPii') }}</span>
          </label>
        </section>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="create()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .form-grid { display: grid; gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class EventHubWebhookSubscriptionsComponent implements OnInit {
  private readonly api = inject(EventHubApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly rows = signal<EventHubWebhookSubscriptionListItem[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly createDialogVisible = signal(false);

  createModel = {
    name: '',
    eventType: 'insurance.quote.created',
    endpointUrl: '',
    allowedDomainsText: '',
    deliverPii: false
  };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listWebhookSubscriptions().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('eventHub.webhooks.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  create(): void {
    const request: CreateEventHubWebhookSubscriptionRequest = {
      name: this.createModel.name.trim(),
      eventType: this.createModel.eventType.trim(),
      endpointUrl: this.createModel.endpointUrl.trim(),
      allowedDomains: this.createModel.allowedDomainsText.split(',').map(item => item.trim()).filter(Boolean),
      maxDeliveriesPerMinute: 60,
      deliverPii: this.createModel.deliverPii,
      isActive: true,
      maxAttempts: 5
    };
    this.api.createWebhookSubscription(request).subscribe({
      next: created => {
        this.createDialogVisible.set(false);
        void this.router.navigate(['/event-hub/webhook-subscriptions', created.id]);
      },
      error: () => this.error.set(this.t('eventHub.webhooks.errorCreate'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
